import { type CSSProperties, type MouseEvent, type ReactNode, useEffect, useRef, useState } from 'react';
import { animate, motion, useInView, useIsomorphicLayoutEffect, useReducedMotion, useSpring, useTransform } from 'framer-motion';
import { isHydrating } from '@/lib/hydration';
import { cn } from '@/lib/utils';
import { SKIP_ENTRANCE } from './motionPrefs';

export const EASE = [0.22, 1, 0.36, 1] as const;

/**
 * Fades content in as it scrolls into view. Driven by CSS so it works on pre-built pages:
 * the server renders plain visible markup, and only content still below the fold at
 * hydration is hidden (off screen) and revealed on scroll.
 */
export function Reveal({ children, className, delay = 0, y = 24 }: { children: ReactNode; className?: string; delay?: number; y?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [hydratedMount] = useState(isHydrating);
  const [phase, setPhase] = useState<'static' | 'hidden' | 'shown'>('static');
  useIsomorphicLayoutEffect(() => {
    const el = ref.current;
    if (!el || SKIP_ENTRANCE || typeof IntersectionObserver === 'undefined' || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (hydratedMount && el.getBoundingClientRect().top < window.innerHeight) return;
    setPhase('hidden');
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {
        setPhase('shown');
        observer.disconnect();
      }
    }, { rootMargin: '0px 0px -40px 0px' });
    observer.observe(el);
    return () => observer.disconnect();
  }, [hydratedMount]);
  return (
    <div
      ref={ref}
      className={cn(className, phase !== 'static' && 'bv-reveal', phase === 'shown' && 'bv-reveal-in')}
      style={phase === 'static' ? undefined : ({ '--bv-reveal-y': `${y}px`, transitionDelay: `${delay}s` } as CSSProperties)}
    >
      {children}
    </div>
  );
}

export function Eyebrow({ children, tone = 'light', live = false, className }: { children: ReactNode; tone?: 'light' | 'dark'; live?: boolean; className?: string }) {
  return (
    <span className={cn('inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[13px] font-semibold', tone === 'light' ? 'border-bv-blue-line bg-white/80 text-bv-blue' : 'border-white/15 bg-white/[0.06] text-bv-blue-bright', className)}>
      {live ? (
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-current opacity-60" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-current" />
        </span>
      ) : (
        <span className="h-1.5 w-1.5 rounded-full bg-current" />
      )}
      {children}
    </span>
  );
}

export function SectionHeading({ eyebrow, title, description, tone = 'light', align = 'left', id, className }: { eyebrow?: ReactNode; title: ReactNode; description?: ReactNode; tone?: 'light' | 'dark'; align?: 'left' | 'center'; id?: string; className?: string }) {
  return (
    <div className={cn('max-w-3xl', align === 'center' && 'mx-auto text-center', className)}>
      {eyebrow && <Reveal><Eyebrow tone={tone}>{eyebrow}</Eyebrow></Reveal>}
      <Reveal delay={0.05}>
        <h2 id={id} className={cn('mt-5 font-display text-[34px] font-semibold leading-[1.06] tracking-[-0.03em] sm:text-5xl', tone === 'light' ? 'text-bv-ink' : 'text-white')}>{title}</h2>
      </Reveal>
      {description && (
        <Reveal delay={0.1}>
          <p className={cn('mt-5 text-lg leading-8', tone === 'light' ? 'text-bv-ink2' : 'text-white/70')}>{description}</p>
        </Reveal>
      )}
    </div>
  );
}

/** Card with a soft light that follows the pointer. */
export function SpotlightCard({ children, className, tone = 'light' }: { children: ReactNode; className?: string; tone?: 'light' | 'dark' }) {
  const ref = useRef<HTMLDivElement>(null);
  const onMove = (event: MouseEvent<HTMLDivElement>) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    el.style.setProperty('--mx', `${event.clientX - rect.left}px`);
    el.style.setProperty('--my', `${event.clientY - rect.top}px`);
  };
  return (
    <div ref={ref} onMouseMove={onMove} className={cn('bv-spotlight group relative overflow-hidden rounded-3xl border transition-[transform,box-shadow] duration-300 hover:-translate-y-1', tone === 'light' ? 'border-bv-blue-line bg-white hover:shadow-bv-window' : 'border-white/10 bg-white/[0.04]', className)}>
      {children}
    </div>
  );
}

/** Counts up once when scrolled into view. */
export function CountUp({ to, duration = 1.6, className, prefix = '', suffix = '' }: { to: number; duration?: number; className?: string; prefix?: string; suffix?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  const reduced = useReducedMotion();
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (reduced) { setValue(to); return; }
    if (!inView) return;
    const controls = animate(0, to, { duration, ease: EASE, onUpdate: setValue });
    return () => controls.stop();
  }, [inView, to, duration, reduced]);
  return <span ref={ref} className={className}>{prefix}{Math.round(value).toLocaleString('en-US')}{suffix}</span>;
}

/** Smoothly springs between values whenever `value` changes. */
export function AnimatedNumber({ value, format }: { value: number; format: (n: number) => string }) {
  const spring = useSpring(value, { stiffness: 90, damping: 20 });
  useEffect(() => { spring.set(value); }, [spring, value]);
  const text = useTransform(spring, v => format(v));
  return <motion.span>{text}</motion.span>;
}

const ringTone = {
  success: 'hsl(var(--good))',
  blue: 'hsl(var(--blue))',
  warn: 'hsl(var(--orange))',
} as const;

export function ScoreRing({ score, size = 56, stroke = 5, tone = 'success', className }: { score: number; size?: number; stroke?: number; tone?: keyof typeof ringTone; className?: string }) {
  const reduced = useReducedMotion();
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  return (
    <span className={cn('relative inline-flex shrink-0 items-center justify-center', className)} style={{ width: size, height: size }} role="img" aria-label={`Score ${score}%`}>
      <svg width={size} height={size} className="-rotate-90" aria-hidden>
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="hsl(var(--blue-line))" strokeWidth={stroke} />
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={ringTone[tone]}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          initial={reduced ? false : { strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: circumference * (1 - score / 100) }}
          transition={{ duration: 1.2, ease: EASE }}
        />
      </svg>
      <span className="absolute font-display text-[13px] font-semibold tabular-nums text-bv-ink" style={{ fontSize: size < 50 ? 12 : 15 }}>{score}</span>
    </span>
  );
}
